import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, User2, ArrowRight } from "lucide-react";
import { get, limitToLast, query, ref } from "firebase/database";

import { database } from "../../firebase/firebase";
import LoadingScreen from "../../utils/LoadingScreen";
import { Title } from "../components/settings/Title";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const LIMIT = 500;

export default function ClientsPage() {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<any[]>([]);
  const [groupedClients, setGroupedClients] = useState<Record<string, any[]>>(
    {}
  );
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  const normalizePhone = (phone: string) =>
    phone.toString().replace(/[^\d]/g, "");

  const groupClients = useCallback((data: any) => {
    const grouped: Record<string, any> = {};
    Object.values(data).forEach((item: any) => {
      const normalizedPhone = normalizePhone(item.phone || "unknown");
      if (!normalizedPhone || normalizedPhone === "0") return;
      if (!grouped[normalizedPhone]) {
        grouped[normalizedPhone] = {
          phone: normalizedPhone,
          name: item.name,
          entries: [],
        };
      }
      grouped[normalizedPhone].entries.push(item);
    });
    return Object.values(grouped);
  }, []);

  const groupByInitialLetter = useCallback((clientsList: any[]) => {
    const groupedByLetter: Record<string, any[]> = {};
    clientsList.forEach((client) => {
      const initial = client.name?.[0]?.toUpperCase() || "#";
      if (!groupedByLetter[initial]) {
        groupedByLetter[initial] = [];
      }
      groupedByLetter[initial].push(client);
    });
    return groupedByLetter;
  }, []);

  const filterClients = useCallback(() => {
    if (!searchTerm) {
      return groupByInitialLetter(clients);
    }
    const filtered = clients.filter(
      (client: any) =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phone.includes(searchTerm)
    );
    return groupByInitialLetter(filtered);
  }, [searchTerm, clients, groupByInitialLetter]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsRef = query(ref(database, "/booking"), limitToLast(LIMIT));
        const snapshot = await get(clientsRef);
        if (snapshot.exists()) {
          const rawData = snapshot.val();
          const groupedData = groupClients(rawData);
          setClients(groupedData);
          setGroupedClients(groupByInitialLetter(groupedData));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [groupByInitialLetter, groupClients]);

  useEffect(() => {
    setGroupedClients(filterClients());
  }, [searchTerm, filterClients]);

  if (loading) {
    return <LoadingScreen />;
  }

  const totalClients = Object.values(groupedClients).reduce(
    (total, group) => total + group.length,
    0
  );

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-white">
      <header className="mb-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/10"
          onClick={() => navigate("/admin-management/menu")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">
            Relacionamento
          </p>
          <h1 className="text-2xl font-semibold">Clientes</h1>
        </div>
      </header>

      <div className="space-y-4">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-3">
          <div className="flex items-center gap-2">
            <User2 className="h-4 w-4 text-white/60" />
            <Input
              placeholder="Buscar por nome ou telefone"
              className="border-0 bg-transparent px-0 text-white placeholder:text-white/40 focus-visible:ring-0"
              onChange={(ev) => setSearchTerm(ev.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Title>Clientes</Title>
          <span className="text-sm text-white/60">Total: {totalClients}</span>
        </div>
        {totalClients === 0 && (
          <p className="text-center text-sm text-white/60">
            Nenhum cliente para mostrar.
          </p>
        )}
        {Object.entries(groupedClients)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([letter, grouped]) => (
            <section key={letter} className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-[0.4em] text-white/60">
                {letter}
              </div>
              {grouped.map((client: any) => (
                <button
                  key={client.phone}
                  onClick={() =>
                    navigate(`/admin-management/others/clients/${client.phone}`)
                  }
                  className="flex w-full items-center justify-between rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-2">
                    <User2 className="h-4 w-4 text-white/70" />
                    <div>
                      <p className="text-sm font-semibold">{client.name}</p>
                      <p className="text-xs text-white/50">{client.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/60">
                    <span className="rounded-full bg-white/10 px-2 py-1">
                      {client.entries.length} viagens
                    </span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </button>
              ))}
            </section>
          ))}
      </div>
    </div>
  );
}
